import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import { Tab, Tabs } from '@mui/material';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import { ConfigurationTabType } from '../../common/model/type/configuration-tab.type';
import ConfigurationSystem from './ConfigurationSystem';
import ConfigurationUserInfoRequiredFields from './ConfigurationUserInfoRequiredFields';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import SettingsIcon from '@mui/icons-material/Settings';
import ConfigurationErrorMessages from './ConfigurationErrorMessages';
import HormonePatternContents from './HormonePatternContents';
import { last } from 'lodash';
import StrawStageContents from './StrawStageContents';
import CurrentCycleContents from './CurrentCycleContents';

const Configuration = () => {
  const { t } = useTranslation();
  const url = useRouteUrl();
  let location = useLocation();

  const [activeTab, setActiveTab] = useState<ConfigurationTabType>(
    ConfigurationTabType.SYSTEM
  );

  const changeTab = (event: any, newValue: ConfigurationTabType) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const setActiveTabFromUrl = () => {
      const pathName = last(
        location.pathname.split('/')
      ) as ConfigurationTabType;
      const validTabs = Object.values(ConfigurationTabType);
      if (validTabs.includes(pathName)) {
        setActiveTab(pathName);
      } else {
        setActiveTab(ConfigurationTabType.SYSTEM);
      }
    };

    setActiveTabFromUrl();
  }, [location.pathname]);

  return (
    <Container>
      <OvPageTitle
        title={t('configurationSettings.title')}
        icon={<SettingsIcon />}
      />
      <ContentWrapper>
        <TabsContainer>
          <Tabs value={activeTab} onChange={changeTab}>
            <Tab
              value="system"
              label={t('configurationSettings.tabs.system')}
              component={Link}
              to={`${url}/system`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value="user-info-required-fields"
              label={t('configurationSettings.tabs.userInfoRequiredFields')}
              component={Link}
              to={`${url}/user-info-required-fields`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value="error-messages"
              label={t('configurationSettings.tabs.errorMessages')}
              component={Link}
              to={`${url}/error-messages`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value="hormone-pattern-contents"
              label={t('configurationSettings.tabs.hormonePatternContents')}
              component={Link}
              to={`${url}/hormone-pattern-contents`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value="straw-stage-contents"
              label={t('configurationSettings.tabs.strawStageContents')}
              component={Link}
              to={`${url}/straw-stage-contents`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value="current-cycle-contents"
              label={t('configurationSettings.tabs.currentCycleContents')}
              component={Link}
              to={`${url}/current-cycle-contents`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
          </Tabs>
        </TabsContainer>

        <Routes>
          <Route index element={<Navigate to={`${url}/system`} replace />} />
          <Route path="system/*" element={<ConfigurationSystem />} />
          <Route
            path="user-info-required-fields/*"
            element={<ConfigurationUserInfoRequiredFields />}
          />
          <Route
            path="error-messages/*"
            element={<ConfigurationErrorMessages />}
          />
          <Route
            path="hormone-pattern-contents/*"
            element={<HormonePatternContents />}
          />
          <Route
            path="straw-stage-contents/*"
            element={<StrawStageContents />}
          />
          <Route
            path="current-cycle-contents/*"
            element={<CurrentCycleContents />}
          />
        </Routes>
      </ContentWrapper>
    </Container>
  );
};

export default Configuration;

const Container = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const ContentWrapper = styled.div`
  margin-top: 1.5rem;
`;

const TabsContainer = styled.div`
  margin-bottom: 1.5rem;

  && .MuiTabs-indicator {
    background-color: ${Colours.OV_BASE};
  }
`;
